import { useState, useEffect, useCallback, useMemo } from 'react';
import { getEhrConnections } from 'api/ehr';
import { TEhrConnectionItem } from 'types/ehrConnection';

let globalData: TEhrConnectionItem[] = [];
let globalLoaded = false;
// 真全局同步：任一实例 refresh（连接/断开/登记 custom 后）都广播给所有挂载中的实例，
// 否则各实例各持一份 useState 拷贝，弹窗里的变更传导不到宿主页面
const listeners = new Set<(list: TEhrConnectionItem[]) => void>();

// enabled=false 时不自动拉取（如 onboarding 向导等绑定守卫放行前，未绑定拉必 400）；
// 翻回 true 且全局未加载过才发起
const useEhrConnections = (enabled = true): [TEhrConnectionItem[], boolean, () => void, Set<string>] => {
    const [data, setData] = useState<TEhrConnectionItem[]>(globalData);
    const [loading, setLoading] = useState(!globalLoaded);

    useEffect(() => {
        const listener = (list: TEhrConnectionItem[]) => {
            setData(list);
            setLoading(false);
        };
        listeners.add(listener);
        // 本实例挂载前其他实例可能已拉完：对齐一次全局缓存
        if (globalLoaded) listener(globalData);
        return () => {
            listeners.delete(listener);
        };
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getEhrConnections();
            if (res && !res.error) {
                const list = res.data?.data || [];
                globalData = list;
                globalLoaded = true;
                listeners.forEach((notify) => notify(list));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled && !globalLoaded) {
            fetchData();
        }
    }, [fetchData, enabled]);

    const connectedNames = useMemo(
        () => new Set(data.filter((c) => c.connectionStatus === 'connected').map((c) => c.ehrSystem)),
        [data],
    );

    return [data, loading, fetchData, connectedNames];
};

export default useEhrConnections;
